import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Monitor, Plus, Edit, Trash2, Check, X } from 'lucide-react';
import { getEquipmentCategories, createEquipmentCategory, updateEquipmentCategory, deleteEquipmentCategory } from '../../api';

const EquipmentCategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editValue, setEditValue] = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getEquipmentCategories();
      setCategories(response.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des catégories');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      setLoading(true);
      setError(null);
      await createEquipmentCategory({ name: newName.trim() });
      setNewName('');
      setShowForm(false);
      fetchCategories();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Cette catégorie existe déjà');
      } else {
        setError("Erreur lors de l'ajout");
        console.error(err);
      }
      setLoading(false);
    }
  };

  const startEdit = (cat) => {
    setEditId(cat.id);
    setEditValue(cat.name);
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditValue('');
  };

  const saveEdit = async (id) => {
    if (!editValue.trim()) { cancelEdit(); return; }
    try {
      setLoading(true);
      setError(null);
      await updateEquipmentCategory(id, { name: editValue.trim() });
      cancelEdit();
      fetchCategories();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Ce nom existe déjà');
      } else {
        setError('Erreur lors du renommage');
        console.error(err);
      }
      setLoading(false);
    }
  };

  const handleDelete = async (cat) => {
    const count = cat._count?.equipment || 0;
    if (count > 0) {
      setError(`Impossible de supprimer "${cat.name}" : ${count} équipement(s) lié(s). Réassignez-les d'abord.`);
      return;
    }
    if (!window.confirm(`Supprimer la catégorie "${cat.name}" ?`)) return;
    try {
      setLoading(true);
      setError(null);
      await deleteEquipmentCategory(cat.id);
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
      setLoading(false);
    }
  };

  if (loading && categories.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <Card>
          <CardContent>
            <p className="text-gray-500 py-8 text-center">Chargement...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor size={20} />
            Catégories d'équipements
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
              {error}
            </div>
          )}

          {showForm && (
            <form onSubmit={handleAdd} className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Nom de la nouvelle catégorie"
                  disabled={loading}
                  required
                  autoFocus
                />
                <button
                  type="submit"
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition disabled:opacity-50"
                  disabled={loading || !newName.trim()}
                >
                  <Check size={18} />
                  Créer
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setNewName(''); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-md transition"
                >
                  Annuler
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Les catégories d'équipements sont globales (partagées par toutes les structures).
              </p>
            </form>
          )}

          <div>
            <h3 className="font-semibold text-gray-700 mb-3">
              Catégories ({categories.length})
            </h3>
            {categories.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">Aucune catégorie. Exécutez le seed ou ajoutez-en une ci-dessus.</p>
            ) : (
              <div className="space-y-1">
                {categories.map((cat) => (
                  <div key={cat.id} className="flex items-center justify-between px-3 py-2 border rounded-md hover:bg-gray-50 transition">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {cat.color && (
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                      )}
                      {editId === cat.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="text"
                            className="flex-1 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEdit(cat.id);
                              if (e.key === 'Escape') cancelEdit();
                            }}
                            autoFocus
                          />
                          <button onClick={() => saveEdit(cat.id)} className="p-1 text-green-600 hover:bg-green-50 rounded-full transition" title="Enregistrer">
                            <Check size={16} />
                          </button>
                          <button onClick={cancelEdit} className="p-1 text-gray-500 hover:bg-gray-200 rounded-full transition" title="Annuler">
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <span className="text-gray-800 font-medium truncate">{cat.name}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            {cat._count?.equipment || 0} équipement(s)
                          </span>
                        </>
                      )}
                    </div>
                    {editId !== cat.id && (
                      <div className="flex items-center gap-1 ml-2">
                        <button onClick={() => startEdit(cat)} className="p-1 text-primary hover:bg-primary/5 rounded-full transition" title="Renommer" disabled={loading}>
                          <Edit size={16} />
                        </button>
                        <button onClick={() => handleDelete(cat)} className="p-1 text-red-600 hover:bg-red-50 rounded-full transition" title="Supprimer" disabled={loading}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="fixed bottom-6 right-6 z-50 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition-all hover:scale-105"
          title="Ajouter une catégorie"
        >
          <Plus size={24} />
        </button>
      )}
    </div>
  );
};

export default EquipmentCategoryManagement;
